/**
 * localStorage helpers shared by the launcher (writes) and read by the runtime SDK.
 * Keys and the shape are part of the contract (docs/architecture.md §10, §12).
 *
 * A device can be bound to several circles, so tokens are kept per circle, keyed by the
 * circle identifier `<slug>-<key>` (§6). Launchers from before keys used the bare slug;
 * `findDeviceTokenKey` finds an entry by circle id whatever its key, and the launcher renames them.
 */
import { STORAGE_KEYS } from '@circles/convex';
import { useSyncExternalStore } from 'react';
import { browserLocalStorage, browserSessionStorage, clearAccountKey, readAccountKey, writeAccountKey } from './accountKeyStorage';
import { clearQueryCache } from './liveQuery';

export type DeviceTokenEntry = {
  token: string;
  circleId: string;
  circleName: string;
  memberId: string;
  memberName: string;
};

export type DeviceTokens = Record<string, DeviceTokenEntry>;

function read(): DeviceTokens {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.deviceTokens);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as DeviceTokens) : {};
  } catch {
    return {};
  }
}

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function write(tokens: DeviceTokens): void {
  localStorage.setItem(STORAGE_KEYS.deviceTokens, JSON.stringify(tokens));
  notify();
}

/** All bound circles on this device, keyed by circle identifier (older entries: bare slug). */
export function getDeviceTokens(): DeviceTokens {
  return read();
}

/** Calls `onChange` whenever this page or another tab changes the device tokens. */
export function subscribeDeviceTokens(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === STORAGE_KEYS.deviceTokens) onChange();
  };
  listeners.add(onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}

let snapshot: { raw: string | null; tokens: DeviceTokens } | null = null;

/** The same object until the stored value changes, as `useSyncExternalStore` needs. */
function tokensSnapshot(): DeviceTokens {
  const raw = localStorage.getItem(STORAGE_KEYS.deviceTokens);
  if (!snapshot || snapshot.raw !== raw) snapshot = { raw, tokens: read() };
  return snapshot.tokens;
}

const NO_TOKENS: DeviceTokens = {};

/** The device tokens, re-rendering on every change (joins, leaves, renames, other tabs). */
export function useDeviceTokens(): DeviceTokens {
  return useSyncExternalStore(subscribeDeviceTokens, tokensSnapshot, () => NO_TOKENS);
}

export function getDeviceToken(circleKey: string): DeviceTokenEntry | null {
  return read()[circleKey] ?? null;
}

/** The storage key of this device's entry for a circle (by its Convex id), whatever key it was stored under. */
export function findDeviceTokenKey(circleId: string): string | null {
  const tokens = read();
  return Object.keys(tokens).find((k) => tokens[k]?.circleId === circleId) ?? null;
}

/** Moves an entry to a new key (an older bare-slug entry to the circle identifier). An entry already at `to` wins. */
export function renameDeviceToken(from: string, to: string): void {
  if (from === to) return;
  const tokens = read();
  const entry = tokens[from];
  if (!entry) return;
  delete tokens[from];
  if (!tokens[to]) tokens[to] = entry;
  write(tokens);
}

/** Adds/replaces the token for a circle and mirrors it to the single-token fallback key. */
export function addDeviceToken(circleKey: string, entry: DeviceTokenEntry): void {
  const tokens = read();
  tokens[circleKey] = entry;
  write(tokens);
  setActiveDeviceToken(entry.token);
}

/** Also forgets every cached query result (lib/liveQuery.ts): they may show the circle just left. */
export function removeDeviceToken(circleKey: string): void {
  const tokens = read();
  const removed = tokens[circleKey];
  delete tokens[circleKey];
  clearQueryCache();
  write(tokens);
  if (removed && localStorage.getItem(STORAGE_KEYS.deviceToken) === removed.token) {
    const next = Object.values(tokens)[0];
    if (next) setActiveDeviceToken(next.token);
    else localStorage.removeItem(STORAGE_KEYS.deviceToken);
  }
}

/** The most recently used token; the SDK's fallback when no per-circle entry matches. */
export function setActiveDeviceToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.deviceToken, token);
}

/** The builder key, whether it was kept on this browser (`localStorage`) or for this tab only (`sessionStorage`). */
export function getAccountKey(): string | null {
  return readAccountKey(browserLocalStorage(), browserSessionStorage());
}

/**
 * A different key (or none) also forgets every cached query result — before the key is written, so
 * on a session-only sign-in the clear still targets `localStorage` and no cached result from before
 * is left behind there (`cacheStorageChoice`, §14.78). `keep` (default true) follows the sheet's
 * "Keep me signed in on this browser": unchecked keeps the key for this tab only (`sessionStorage`).
 * Clearing with null signs out of both storages.
 */
export function setAccountKey(accountKey: string | null, opts?: { keep?: boolean }): void {
  if (accountKey !== getAccountKey()) clearQueryCache();
  const local = browserLocalStorage();
  const session = browserSessionStorage();
  if (accountKey) writeAccountKey(local, session, accountKey, opts?.keep ?? true);
  else clearAccountKey(local, session);
}
